import * as React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Building2, Check, ChevronsUpDown, Plus, Search, UserPlus } from 'lucide-react';
import {
    SidebarMenu,
    SidebarMenuItem,
    SidebarMenuButton,
    useSidebar,
} from '@/components/ui/sidebar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useCurrentOrganization } from '@/features/organization/hooks/use-current-organization';
import { useOrganizations } from '@/features/organization/hooks/use-organizations';
import { useSwitchOrganization } from '@/features/organization/hooks/use-switch-organization';
import { ORGANIZATION_QUERY_KEYS } from '@/features/organization/constants/organization.constants';
import { CreateOrganizationDialog } from '@/features/organization/components/create-organization-dialog';
import { IS_AGENCY_MODULE_ENABLED } from '@/features/agency/feature-flag';
import { useMyAgency } from '@/features/agency/hooks/use-agency';
import { PRIVATE_ROUTES } from '@/router/constant/routes';

function OrgAvatar({
    name,
    logoUrl,
    size = 'md',
    onLogoError,
}: {
    name: string;
    logoUrl?: string | null;
    size?: 'sm' | 'md';
    onLogoError?: () => void;
}) {
    const { t } = useTranslation();
    const initial = name?.charAt(0).toUpperCase() ?? '?';
    const sizeClass = size === 'sm' ? 'size-6 text-xs' : 'size-8 text-sm';

    const [failedBasePath, setFailedBasePath] = React.useState<string | null>(null);
    const hasInvalidatedRef = React.useRef(false);

    // Use only the base path (before '?') so that re-issued presigned URLs for the
    // same S3 object don't reset the guard — only a genuinely new logo (new UUID
    // in the path) should clear imgFailed and allow another invalidation attempt.
    const logoBasePath = logoUrl ? (logoUrl.split('?')[0] ?? null) : null;
    const imgFailed = failedBasePath !== null && failedBasePath === logoBasePath;

    React.useEffect(() => {
        hasInvalidatedRef.current = false;
    }, [logoBasePath]);

    const handleImgError = () => {
        setFailedBasePath(logoBasePath);
        if (!hasInvalidatedRef.current) {
            hasInvalidatedRef.current = true;
            onLogoError?.();
        }
    };

    if (logoUrl && !imgFailed) {
        return (
            <img
                src={logoUrl}
                alt={t('organization.logo.altText', { name })}
                className={cn('rounded-md object-cover', sizeClass)}
                onError={handleImgError}
            />
        );
    }

    return (
        <div
            className={cn(
                'flex shrink-0 items-center justify-center rounded-md bg-primary-100 font-semibold text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
                sizeClass,
            )}
        >
            {initial}
        </div>
    );
}

export function TeamSwitcher() {
    const { t } = useTranslation();
    const [search, setSearch] = React.useState('');
    const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
    const { isMobile, open: sidebarOpen } = useSidebar();

    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { data: currentOrg, isLoading: isLoadingCurrent } = useCurrentOrganization();
    const { data: allOrgs, isLoading: isLoadingAll } = useOrganizations();
    const { data: myAgency } = useMyAgency();
    const switchOrg = useSwitchOrganization();

    const logoUrl = currentOrg?.settings?.logoUrl ?? null;

    const handleLogoError = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.current });
    }, [queryClient]);

    const filteredOrgs = React.useMemo(() => {
        if (!allOrgs) return [];
        if (!search.trim()) return allOrgs;
        return allOrgs.filter((o) => o.name.toLowerCase().includes(search.toLowerCase()));
    }, [allOrgs, search]);

    if (isLoadingCurrent) {
        return (
            <SidebarMenu>
                <SidebarMenuItem>
                    <div className="flex items-center gap-3 p-2">
                        <Skeleton className="size-8 rounded-md" />
                        {sidebarOpen && <Skeleton className="h-4 w-28" />}
                    </div>
                </SidebarMenuItem>
            </SidebarMenu>
        );
    }

    if (!currentOrg) return null;

    return (
        <>
            <SidebarMenu>
                <SidebarMenuItem>
                    <DropdownMenu onOpenChange={(open) => !open && setSearch('')}>
                        <DropdownMenuTrigger asChild>
                            <SidebarMenuButton
                                size="lg"
                                aria-label={t('organization.switcher.createButton')}
                                className="cursor-pointer transition-colors hover:bg-bg-hover! data-[state=open]:bg-bg-selected! data-[state=open]:text-sidebar-accent-foreground"
                            >
                                <OrgAvatar
                                    name={currentOrg.name}
                                    logoUrl={logoUrl}
                                    onLogoError={handleLogoError}
                                />
                                {sidebarOpen && (
                                    <>
                                        <div className="grid min-w-0 flex-1 text-left leading-tight">
                                            <span className="truncate text-sm font-medium">
                                                {currentOrg.name}
                                            </span>
                                            <span className="truncate text-xs capitalize text-text-secondary">
                                                {currentOrg.plan.toLowerCase()}
                                            </span>
                                        </div>
                                        <ChevronsUpDown className="ml-auto size-4 shrink-0" />
                                    </>
                                )}
                            </SidebarMenuButton>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent
                            className="w-(--radix-dropdown-menu-trigger-width) min-w-60 rounded-lg p-0"
                            side={isMobile ? 'bottom' : 'right'}
                            align="start"
                            sideOffset={4}
                        >
                            {/* Search */}
                            <div className="flex items-center gap-2 px-3 py-2.5">
                                <Search className="size-3.5 shrink-0 text-text-tertiary" />
                                <input
                                    type="text"
                                    placeholder={t('organization.switcher.searchPlaceholder')}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    className="flex-1 bg-transparent text-sm outline-none placeholder:text-text-tertiary"
                                    autoFocus
                                />
                            </div>

                            <DropdownMenuSeparator className="my-0" />

                            {/* Agency console shortcut — only for agency members,
                                and only while the console is not withdrawn. */}
                            {IS_AGENCY_MODULE_ENABLED && myAgency && (
                                <>
                                    <div className="py-1">
                                        <DropdownMenuItem
                                            className="flex items-center gap-2.5 px-3 py-2.5"
                                            onSelect={() => navigate(PRIVATE_ROUTES.AGENCY)}
                                        >
                                            <div className="flex size-6 items-center justify-center rounded-md bg-primary-100 dark:bg-primary-900/30">
                                                <Building2 className="size-3.5 text-primary-700 dark:text-primary-300" />
                                            </div>
                                            <span className="flex-1 truncate text-sm font-medium">
                                                {myAgency.name}
                                            </span>
                                            <span className="text-xs text-text-tertiary">
                                                {t('agency.switcher.agencyLabel', 'Agency')}
                                            </span>
                                        </DropdownMenuItem>
                                    </div>
                                    <DropdownMenuSeparator className="my-0" />
                                </>
                            )}

                            {/* Org list */}
                            <div className="max-h-52 overflow-y-auto py-1">
                                {isLoadingAll ? (
                                    <div className="space-y-1 p-2">
                                        {[1, 2].map((i) => (
                                            <div
                                                key={i}
                                                className="flex items-center gap-2 px-2 py-1.5"
                                            >
                                                <Skeleton className="size-6 rounded-md" />
                                                <Skeleton className="h-4 w-28" />
                                            </div>
                                        ))}
                                    </div>
                                ) : filteredOrgs.length === 0 ? (
                                    <p className="px-3 py-4 text-center text-xs text-text-secondary">
                                        {t('organization.switcher.empty')}
                                    </p>
                                ) : (
                                    filteredOrgs.map((org) => {
                                        const isActive = org.id === currentOrg.id;

                                        return (
                                            <DropdownMenuItem
                                                key={org.id}
                                                className={cn(
                                                    'flex items-center gap-2.5 px-3 py-2.5',
                                                    isActive &&
                                                        'font-medium text-primary-700 dark:text-primary-300',
                                                )}
                                                onSelect={() => {
                                                    if (!isActive) switchOrg.mutate(org.id);
                                                }}
                                                disabled={
                                                    switchOrg.isPending &&
                                                    switchOrg.variables === org.id
                                                }
                                            >
                                                <OrgAvatar
                                                    name={org.name}
                                                    size="sm"
                                                    logoUrl={org.settings?.logoUrl}
                                                />
                                                <span className="flex-1 truncate text-sm">
                                                    {org.name}
                                                </span>
                                                {isActive && (
                                                    <Check className="ml-auto size-3.5 shrink-0 text-primary-600" />
                                                )}
                                            </DropdownMenuItem>
                                        );
                                    })
                                )}
                            </div>

                            <DropdownMenuSeparator className="my-0" />

                            {/* Create org / add agency client */}
                            <div className="py-1">
                                {IS_AGENCY_MODULE_ENABLED && myAgency && (
                                    <DropdownMenuItem
                                        className="flex items-center gap-2 px-3 py-2.5 text-sm"
                                        onSelect={() => navigate(PRIVATE_ROUTES.AGENCY_CLIENT_NEW)}
                                    >
                                        <div className="flex size-6 items-center justify-center rounded-md bg-neutral-100 dark:bg-neutral-800">
                                            <UserPlus className="size-3.5 text-text-secondary" />
                                        </div>
                                        <span>{t('agency.switcher.addClient', 'Add Client')}</span>
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                    className="flex items-center gap-2 px-3 py-2.5 text-sm"
                                    onSelect={() => setCreateDialogOpen(true)}
                                >
                                    <div className="flex size-6 items-center justify-center rounded-md bg-neutral-100 dark:bg-neutral-800">
                                        <Plus className="size-3.5 text-text-secondary" />
                                    </div>
                                    <span>{t('organization.switcher.createButton')}</span>
                                </DropdownMenuItem>
                            </div>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </SidebarMenuItem>
            </SidebarMenu>

            <CreateOrganizationDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />
        </>
    );
}
